import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Button, Steps, Alert, message } from 'antd';
import { ImportOutlined } from '@ant-design/icons';
import { TrainingProjectDashboardStore } from '../../stores';
import { PackageSelectionStep, ImportPreviewStep } from './wizard_steps';

const { Step } = Steps;

interface Props {
    store: TrainingProjectDashboardStore;
}

const ImportTrainingProjectExamplesModal: React.FC<Props> = ({ store }) => {
    const [current, setCurrent] = React.useState(0);
    const [selectedPackages, setSelectedPackages] = React.useState<string[]>([]);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    const packageSelectionStep = 0;
    const importPreviewStep = 1;

    // Reset wizard when modal is closed/opened
    React.useEffect(() => {
        if (!store.importExamplesDialogVisible) {
            setCurrent(packageSelectionStep);
            setSelectedPackages([]);
            setErrorMessage(null);
        } else {
            store.clearJsonSchemaDifferences();
        }
    }, [store.importExamplesDialogVisible, packageSelectionStep, store]);

    const onClose = () => {
        store.setImportExamplesDialogVisible(false);
    };

    const nextStep = () => {
        if (current === packageSelectionStep && selectedPackages.length === 0) {
            setErrorMessage('Please select at least one package!');
            return;
        }

        setErrorMessage(null);
        setCurrent(Math.min(current + 1, 1));
    };

    const previousStep = () => {
        setErrorMessage(null);
        setCurrent(Math.max(current - 1, 0));
    };

    const handleImport = async () => {
        if (selectedPackages.length === 0) {
            message.error('No packages selected for import');
            return;
        }

        const validPackages = store.preparedPackagesForImport
            .filter(p => selectedPackages.includes(p.packageId) && !p.hasInvalidSchema && !p.isDuplicate)
            .map(p => p.packageId);

        if (validPackages.length === 0) {
            message.error('No valid packages selected for import');
            return;
        }

        const existingExampleMappings = store.preparedPackagesForImport
            .filter(p => selectedPackages.includes(p.packageId) && !p.isDuplicate && p.existingExampleId)
            .map(p => ({ packageId: p.packageId, exampleId: p.existingExampleId! }));

        const success = await store.importExamplesFromPackages(validPackages, existingExampleMappings);
        if (success) {
            onClose();
        }
    };

    const steps = [
        { key: 'packages', title: 'Select Packages' },
        { key: 'preview', title: 'Preview & Import' }
    ];

    const getContent = () => {
        switch (current) {
            case packageSelectionStep:
                return (
                    <PackageSelectionStep
                        store={store}
                        selectedPackages={selectedPackages}
                        setSelectedPackages={setSelectedPackages}
                    />
                );
            case importPreviewStep:
                return <ImportPreviewStep store={store} selectedPackages={selectedPackages} />;
            default:
                return null;
        }
    };

    const getFooterButtons = () => {
        const buttons = [];

        // Cancel button (always visible)
        buttons.push(
            <Button key="cancel" size="large" onClick={onClose}>
                Cancel
            </Button>
        );

        // Back button (visible on preview step)
        if (current === importPreviewStep) {
            buttons.push(
                <Button key="back" size="large" onClick={previousStep}>
                    Back
                </Button>
            );
        }

        // Next/Import button
        if (current === packageSelectionStep) {
            buttons.push(
                <Button key="next" size="large" type="primary" onClick={nextStep}>
                    Next
                </Button>
            );
        } else if (current === importPreviewStep) {
            const validExamplesCount = store.preparedPackagesForImport.filter(
                pkg => !pkg.hasInvalidSchema && !pkg.isDuplicate
            ).length;
            const hasValidExamples = validExamplesCount > 0;

            buttons.push(
                <Button
                    key="import"
                    size="large"
                    type="primary"
                    icon={<ImportOutlined />}
                    onClick={handleImport}
                    loading={store.isLoadingImportOperations}
                    disabled={!hasValidExamples}
                >
                    Import {hasValidExamples ? `(${validExamplesCount})` : '(0)'} Examples
                </Button>
            );
        }

        return buttons;
    };

    return (
        <Modal
            className="import-training-project-examples-dialog"
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ImportOutlined />
                    Import Examples
                </div>
            }
            visible={store.importExamplesDialogVisible}
            width={1100}
            onCancel={onClose}
            maskClosable={false}
            closable
            destroyOnClose
            centered
            footer={getFooterButtons()}
        >
            <Steps current={current} style={{ marginBottom: 24 }}>
                {steps.map(item => (
                    <Step key={item.key} title={item.title} />
                ))}
            </Steps>

            {errorMessage && <Alert message={errorMessage} style={{ marginBottom: 16 }} type="error" showIcon />}

            {getContent()}
        </Modal>
    );
};

export default observer(ImportTrainingProjectExamplesModal);
